import { useMemo } from 'react';
import { useRSimsSimsContext } from 'rSimInventory/hooks/useRSimsSimsContext';
import { getSessionStatusTranslatedLabel, SessionStatusUi } from 'simInventory/utils/sessionStatus';
import { Actions, Subjects } from 'permissions/ability';
import { useTranslation } from 'react-i18next';
import { ColumnConfiguration } from 'src/common/components/ConfigurableTable/models/configurableTable';
import { SimDataKeys } from 'src/common/components/Sim/constants/sim';

const FORMATERS_MAP: Partial<Record<SimDataKeys, (value: any) => string>> = {
  sessionStatus: (value: any) => getSessionStatusTranslatedLabel(value as SessionStatusUi, '-'),
};

const SIZE_CONFIGURATION: Partial<
  Record<SimDataKeys, { width?: number; minWidth?: number; maxWidth?: number }>
> = {
  iccid: {
    width: 190,
  },
  currentIpAddress: {
    width: 130,
  },
  managementIpAddress: {
    width: 120,
  },
  commsPlan: {
    width: 240,
  },
  mobileNetworkOperator: {
    width: 240,
  },
  mnoStatus: {
    width: 150,
  },
  msisdn: {
    width: 170,
  },
  liveDataUsage: {
    width: 150,
  },
  inMinuteUsage: {
    width: 150,
  },
  outMinuteUsage: {
    width: 150,
  },
  inTextUsage: {
    width: 150,
  },
  outTextUsage: {
    width: 150,
  },
  sessionStartTime: {
    width: 170,
  },
  connectionId: {
    width: 150,
  },
  sessionStatus: {
    width: 160,
  },
};

export const useColumnConfiguration = () => {
  const { data } = useRSimsSimsContext();
  const { t } = useTranslation();

  const idLabelMap: Partial<Record<SimDataKeys, string>> = useMemo(
    () => ({
      iccid: t('common.iccid'),
      imsi: t('common.imsi'),
      msisdn: t('common.msisdn'),
      currentIpAddress: t('products.sim.lastKnownIP'),
      managementIpAddress: t('common.managementIp'),
      mnoRatePlan: t('common.plan'),
      organisation: t('common.organisation'),
      label: t('common.label'),
      mnoStatus: t('simInventory.mnoStatus'),
      mnoStatusEntryTime: t('simInventory.mnoStatusEntryTime_combined'),
      customerCommsPlan: t('simInventory.customerCommsPlan'),
      commsPlan: t('simInventory.commsPlan'),
      mobileNetworkOperator: t('simInventory.mobileNetworkOperator'),
      accountName: t('common.accountName'),
      accountRef: t('common.accountNumber'),
      simStatus: t('simInventory.simStatus'),
      liveDataUsage: t('common.data'),
      inMinuteUsage: t('common.inMinuteUsageProduct'),
      outMinuteUsage: t('common.outMinuteUsageProduct'),
      inTextUsage: t('common.inTextUsageProduct'),
      outTextUsage: t('common.outTextUsageProduct'),
      sessionStartTime: t('simInventory.sessionStartTime'),
      sessionEndTime: t('simInventory.sessionEndTime'),
      sessionStatus: t('simInventory.sessionStatus'),
      sessionStatusLastUpdated: t('simInventory.sessionStatusLastUpdated_combined'),
      connectionId: t('simInventory.connectionId'),
      orderNumber: t('simInventory.orderNumber'),
      tags: t('common.tag'),
    }),
    [],
  );

  const configuration: ColumnConfiguration[] = useMemo(() => {
    const staticHeadersBegining: ColumnConfiguration[] = [
      {
        field: 'status',
        headerName: '',
        width: 50,
        resizable: false,
        sortable: false,
        pinnable: false,
      },
    ];

    const obtainedHeaders =
      data?.headers?.map((header: any) => {
        const field = header.types[0] as SimDataKeys;

        const baseConfiguration: ColumnConfiguration = {
          field: field,
          headerName: idLabelMap[field],
          valueGetter: FORMATERS_MAP[field],
          resizable: false,
          sortable: false,
          pinnable: false,
          width: SIZE_CONFIGURATION[field]?.width,
          minWidth: SIZE_CONFIGURATION[field]?.minWidth,
          maxWidth: SIZE_CONFIGURATION[field]?.maxWidth,
        };

        if (header.types[1]) {
          const subField = header.types[1] as SimDataKeys;
          baseConfiguration.subField = {
            field: subField,
            headerName: idLabelMap[subField] || subField,
            valueGetter: FORMATERS_MAP[subField],
          };
        }

        return baseConfiguration;
      }) || [];

    const staticHeadersEnd: ColumnConfiguration[] = [
      {
        field: 'simulateOutage',
        headerName: t('common.simulateOutage'),
        width: 164,
        resizable: false,
        sortable: false,
        pinnable: false,
        permission: {
          action: Actions.simulate,
          subject: Subjects.outage,
        },
      },
    ];

    return [...staticHeadersBegining, ...obtainedHeaders, ...staticHeadersEnd];
  }, [data?.headers]);

  return configuration;
};
